<template>
  <div class="page-shell mobile-home-page">
    <section class="container mobile-home-hero" style="overflow: hidden;">
      <div class="panel mobile-home-hero__panel">
        <div v-if="loading" class="mobile-home-hero__state status-card">正在加载首页轮播...</div>
        <div v-else-if="errorMessage" class="mobile-home-hero__state status-card is-error">
          {{ errorMessage }}
        </div>
        <div v-else-if="carouselList.length" class="mobile-home-banner">
          <div ref="heroSwiper" class="swiper mobile-home-banner__swiper">
            <div class="swiper-wrapper">
              <button
                v-for="item in carouselList"
                :key="item.id"
                class="swiper-slide mobile-home-banner__slide"
                type="button"
                @click="handleCarouselClick(item.path)"
              >
                <img :src="resolveAssetUrl(item.carousel)" style="object-fit: contain;" :alt="`首页轮播 ${item.id}`">
              </button>
            </div>

            <div ref="heroPagination" class="mobile-home-banner__pagination"></div>
          </div>
        </div>
        <div v-else class="mobile-home-hero__state status-card is-empty">
          暂时没有轮播图数据。
        </div>
      </div>
    </section>

    <section class="container mobile-home-content">
      <div
        v-if="featuredTabs.length"
        class="mobile-home-tabs"
        role="tablist"
        aria-label="移动端首页课程分类"
      >
        <button
          v-for="tab in featuredTabs"
          :key="tab.id"
          type="button"
          :class="['mobile-home-tab', { 'is-active': activeFeaturedTab === tab.id }]"
          role="tab"
          :aria-selected="activeFeaturedTab === tab.id ? 'true' : 'false'"
          @click="activeFeaturedTab = tab.id"
        >
          {{ tab.label }}
        </button>
      </div>

      <div v-if="featuredLoading" class="status-card">正在加载课程推荐...</div>
      <div v-else-if="featuredError" class="status-card is-error">
        {{ featuredError }}
      </div>
      <div v-else-if="featuredCourseList.length" class="mobile-course-list">
        <router-link
          v-for="course in featuredCourseList"
          :key="course.id"
          class="mobile-course-item"
          :to="{ name: 'course-detail', params: { id: String(course.id) } }"
        >
          <div class="mobile-course-item__thumb" :style="courseCardStyle(course)">
            <div
              class="mobile-course-item__thumb-backdrop"
              :style="courseHeroBackdropStyle(course)"
            ></div>

            <div class="mobile-course-item__thumb-copy">
              <span>{{ courseThumbLabel(course) }}</span>
              <strong>{{ courseMonogram(course) }}</strong>
            </div>
          </div>

          <div class="mobile-course-item__body">
            <h2>{{ course.title }}</h2>
            <p>{{ formatCourseSummary(course) }}</p>

            <div class="mobile-course-item__meta">
              <span>{{ courseCategoryLabel(course) }}</span>
              <span>{{ course.serialize_status_word || '更新中' }}</span>
            </div>

            <div class="mobile-course-item__footer">
              <span>{{ courseTeacherLabel(course) }}</span>
              <span>{{ course.teacher_count || 0 }} 位教师</span>
            </div>
          </div>
        </router-link>
      </div>
      <div v-else class="status-card is-empty">
        当前标签下暂时没有课程，试试切换其他分类。
      </div>

      <router-link
        class="mobile-home-more"
        :to="{ name: 'courses', query: activeFeaturedCategoryQuery }"
      >
        查看全部分类
      </router-link>
    </section>
  </div>
</template>

<script>
import Swiper from 'swiper'
import { Autoplay, Pagination } from 'swiper/modules'
import 'swiper/css'
import 'swiper/css/pagination'

import { fetchHomeIndex } from '@/api/home'
import { fetchCourseList } from '@/api/course'
import { getCourseMonogram, getCourseThemeStyle } from '@/utils/courseTheme'
import { resolveAssetUrl } from '@/utils/format'

const MOBILE_RECOMMENDED_TAB_ID = 'hot'
const MOBILE_FEATURED_DISPLAY_LIMIT = 8

export default {
  name: 'MobileHomeView',
  data() {
    return {
      loading: true,
      errorMessage: '',
      homeData: {},
      heroSwiper: null,
      featuredLoading: true,
      featuredError: '',
      featuredCourses: [],
      activeFeaturedTab: MOBILE_RECOMMENDED_TAB_ID
    }
  },
  computed: {
    carouselList() {
      return Array.isArray(this.homeData.carousel_list) ? this.homeData.carousel_list : []
    },
    categoryList() {
      return Array.isArray(this.homeData.course_category_list)
        ? this.homeData.course_category_list
        : []
    },
    featuredTabs() {
      const categoryTabs = this.categoryList
        .filter(category => category && Number(category.id) > 0 && category.name)
        .map(category => ({
          id: `category-${category.id}`,
          label: category.name,
          type: 'category',
          categoryId: Number(category.id)
        }))

      return [
        {
          id: MOBILE_RECOMMENDED_TAB_ID,
          label: '热门',
          type: 'recommended'
        },
        ...categoryTabs
      ]
    },
    activeFeaturedTabMeta() {
      return this.featuredTabs.find(tab => tab.id === this.activeFeaturedTab) || this.featuredTabs[0] || null
    },
    featuredCourseList() {
      const activeTab = this.activeFeaturedTabMeta

      if (!activeTab) {
        return []
      }

      const sourceList = activeTab.type === 'category'
        ? this.featuredCourses.filter(course => (
            Number(course.category_one_id) === activeTab.categoryId
            || Number(course.category_two_id) === activeTab.categoryId
          ))
        : this.featuredCourses

      return sourceList.slice(0, MOBILE_FEATURED_DISPLAY_LIMIT)
    },
    activeFeaturedCategoryQuery() {
      const activeTab = this.activeFeaturedTabMeta

      if (!activeTab || activeTab.type !== 'category') {
        return {}
      }

      return {
        category: String(activeTab.categoryId)
      }
    }
  },
  created() {
    this.loadHome()
    this.loadFeaturedCourses()
  },
  beforeDestroy() {
    this.destroyHeroSwiper()
  },
  methods: {
    resolveAssetUrl,
    courseCardStyle(course) {
      return getCourseThemeStyle(course)
    },
    courseHeroBackdropStyle(course) {
      const cover = this.resolveAssetUrl(course.cover)

      if (!cover) {
        return {}
      }

      return {
        backgroundImage: `linear-gradient(135deg, rgba(9, 16, 40, 0.06) 0%, rgba(9, 16, 40, 0.2) 100%), url(${cover})`
      }
    },
    courseMonogram(course = {}) {
      return getCourseMonogram(course)
    },
    courseThumbLabel(course = {}) {
      return course.category_two_name || course.category_one_name || '精选课程'
    },
    courseCategoryLabel(course = {}) {
      return course.category_two_name || course.category_one_name || '课程专题'
    },
    courseTeacherLabel(course = {}) {
      return course.lecturer_name || '讲师待定'
    },
    stripHtml(value = '') {
      return String(value)
        .replace(/<[^>]*>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
    },
    formatCourseSummary(course = {}) {
      const plainText = this.stripHtml(course.description)

      if (!plainText) {
        return '围绕真实业务场景设计，帮助你更快把知识点转成项目经验。'
      }

      return plainText.length > 52
        ? `${plainText.slice(0, 52).trim()}...`
        : plainText
    },
    ensureActiveFeaturedTab() {
      if (!this.featuredTabs.length) {
        this.activeFeaturedTab = MOBILE_RECOMMENDED_TAB_ID
        return
      }

      if (!this.featuredTabs.some(tab => tab.id === this.activeFeaturedTab)) {
        this.activeFeaturedTab = this.featuredTabs[0].id
      }
    },
    resolveRouteTarget(path) {
      if (path === '/' || path === '/courses' || path === '/login' || path === '/register' || path === '/reset-password' || path === '/profile') {
        return path
      }

      const matchedCourseId = path.match(/^\/courses?\/(\d+)$/)

      if (matchedCourseId) {
        return {
          name: 'course-detail',
          params: {
            id: matchedCourseId[1]
          }
        }
      }

      return null
    },
    async loadHome() {
      this.loading = true
      this.errorMessage = ''
      this.destroyHeroSwiper()

      try {
        this.homeData = await fetchHomeIndex()
        this.ensureActiveFeaturedTab()
      } catch (error) {
        this.errorMessage = error.message || '首页数据加载失败'
      } finally {
        this.loading = false
        await this.$nextTick()
        this.initHeroSwiper()
      }
    },
    async loadFeaturedCourses() {
      this.featuredLoading = true
      this.featuredError = ''

      try {
        const response = await fetchCourseList({
          page: 0
        })

        this.featuredCourses = Array.isArray(response.data) ? response.data : []
        this.ensureActiveFeaturedTab()
      } catch (error) {
        this.featuredError = error.message || '课程推荐加载失败'
        this.featuredCourses = []
      } finally {
        this.featuredLoading = false
      }
    },
    initHeroSwiper() {
      if (!this.$refs.heroSwiper || !this.carouselList.length) {
        return
      }

      this.destroyHeroSwiper()

      this.heroSwiper = new Swiper(this.$refs.heroSwiper, {
        modules: [Autoplay, Pagination],
        loop: this.carouselList.length > 1,
        speed: 680,
        autoplay: this.carouselList.length > 1
          ? {
              delay: 3600,
              disableOnInteraction: false,
              pauseOnMouseEnter: true
            }
          : false,
        pagination: this.$refs.heroPagination
          ? {
              el: this.$refs.heroPagination,
              clickable: true
            }
          : false
      })
    },
    destroyHeroSwiper() {
      if (this.heroSwiper && !this.heroSwiper.destroyed) {
        this.heroSwiper.destroy(true, true)
      }

      this.heroSwiper = null
    },
    handleCarouselClick(path) {
      if (!path) {
        return
      }

      if (/^https?:\/\//i.test(path)) {
        window.open(path, '_blank', 'noopener')
        return
      }

      const normalizedPath = path.startsWith('/') ? path : `/${path}`
      const routeTarget = this.resolveRouteTarget(normalizedPath)

      if (routeTarget) {
        this.$router.push(routeTarget).catch(() => {})
        return
      }

      window.location.href = normalizedPath
    }
  }
}
</script>

<style scoped lang="scss">
.mobile-home-page {
  display: grid;
  gap: 18px;
}

.mobile-home-hero__panel {
  overflow: hidden;
  padding: 0;
  border-radius: 24px;
}

.mobile-home-hero__state {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 180px;
  border: 0;
  border-radius: 0;
}

.mobile-home-banner {
  position: relative;
  overflow: hidden;
  border-radius: 24px;
}

.mobile-home-banner__swiper {
  width: 100%;
  height: auto;
  aspect-ratio: 335 / 182;
  --swiper-theme-color: #{$color-surface};
  --swiper-pagination-bottom: 12px;
  --swiper-pagination-bullet-size: 8px;
  --swiper-pagination-bullet-horizontal-gap: 4px;
  --swiper-pagination-bullet-inactive-color: rgba(255, 255, 255, 0.48);
  --swiper-pagination-bullet-inactive-opacity: 1;
}

.mobile-home-banner__slide {
  width: 100%;
  height: 100%;
  padding: 0;
  border: 0;
  background: #121212;
  overflow: hidden;
}

.mobile-home-banner__slide img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.mobile-home-banner__pagination {
  position: absolute;
  right: 14px;
  bottom: 10px;
  left: auto;
  z-index: 2;
  width: auto;
}

.mobile-home-content {
  display: grid;
  gap: 14px;
}

.mobile-home-tabs {
  height: 48px;
  display: flex;
  gap: 10px;
  overflow-x: auto;
  padding-bottom: 2px;
  scrollbar-width: none;
}

.mobile-home-tabs::-webkit-scrollbar {
  display: none;
}

.mobile-home-tab {
  flex: 0 0 auto;
  min-height: 38px;
  padding: 0 16px;
  border: 1px solid rgba($color-border, 0.98);
  border-radius: 999px;
  background: rgba($color-surface, 0.98);
  color: #4b5563;
  font-size: 15px;
  font-weight: 700;
  white-space: nowrap;
  transition: background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease, transform 0.2s ease;
}

.mobile-home-tab:hover {
  transform: translateY(-1px);
}

.mobile-home-tab.is-active {
  border-color: transparent;
  background: linear-gradient(135deg, #ff2d14 0%, #ff4a39 100%);
  box-shadow: 0 10px 22px rgba(255, 45, 20, 0.18);
  color: $color-surface;
}

.mobile-course-list {
  display: grid;
  gap: 16px;
}

.mobile-course-item {
  display: grid;
  grid-template-columns: 92px minmax(0, 1fr);
  gap: 14px;
  align-items: start;
}

.mobile-course-item__thumb {
  position: relative;
  overflow: hidden;
  min-height: 92px;
  border-radius: 16px;
  background: linear-gradient(135deg, var(--course-theme-primary) 0%, var(--course-theme-secondary) 100%);
  box-shadow: 0 14px 28px rgba(15, 23, 42, 0.12);
}

.mobile-course-item__thumb::after {
  content: '';
  position: absolute;
  right: -18px;
  bottom: -24px;
  width: 54px;
  height: 54px;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.1);
}

.mobile-course-item__thumb-backdrop {
  position: absolute;
  inset: 0;
  background-position: center;
  background-size: cover;
  mix-blend-mode: soft-light;
  opacity: 0.62;
}

.mobile-course-item__thumb-copy {
  position: relative;
  z-index: 1;
  display: grid;
  align-content: center;
  gap: 6px;
  min-height: 92px;
  padding: 14px 12px;
  color: $color-surface;
  text-align: center;
}

.mobile-course-item__thumb-copy span {
  font-size: 13px;
  line-height: 1.35;
}

.mobile-course-item__thumb-copy strong {
  font-size: 20px;
  font-weight: 900;
  letter-spacing: 0.06em;
}

.mobile-course-item__body {
  display: grid;
  gap: 8px;
  min-width: 0;
}

.mobile-course-item__body h2 {
  color: #1f2937;
  font-size: 16px;
  line-height: 1.5;
  display: -webkit-box;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.mobile-course-item__body p {
  color: #667085;
  font-size: 14px;
  line-height: 1.6;
  display: -webkit-box;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.mobile-course-item__meta,
.mobile-course-item__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  flex-wrap: wrap;
}

.mobile-course-item__meta {
  color: #566273;
  font-size: 13px;
  font-weight: 700;
}

.mobile-course-item__footer {
  color: #8d97a5;
  font-size: 12px;
}

.mobile-home-more {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 42px;
  border: 1px solid rgba($color-primary, 0.14);
  border-radius: 999px;
  background: rgba($color-surface, 0.98);
  color: $color-primary;
  font-size: 14px;
  font-weight: 800;
}
</style>
